import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Send, MessageCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return toast.error('Sujet et message requis');
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/contact`, { subject, message }, { withCredentials: true });
      // Brief Jun 16 §2 — surface the real delivery state, but always succeed UX
      // because the message is persisted server-side regardless of email delivery.
      if (data?.email_sent) {
        toast.success('Message envoyé — nous vous répondrons sous 48 h.');
      } else {
        toast.success('Message reçu. Si vous ne recevez pas de réponse sous 48 h, écrivez à info@opensapiens.ch.');
      }
      setSubject(''); setMessage('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Une erreur est survenue. Réessayez ou écrivez à info@opensapiens.ch.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="contact-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Nous contacter</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Une question, un souci, une suggestion ? Écrivez-nous, on vous répond sous 48 h ouvrées.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <a
          href="mailto:info@opensapiens.ch"
          className="p-4 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70 hover:border-purple-300 transition-all"
          data-testid="contact-email-link"
        >
          <Mail className="h-5 w-5 text-purple-600 mb-2" />
          <p className="font-semibold text-gray-900 dark:text-white">Email direct</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">info@opensapiens.ch</p>
        </a>
        <div className="p-4 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70">
          <MessageCircle className="h-5 w-5 text-purple-600 mb-2" />
          <p className="font-semibold text-gray-900 dark:text-white">Support en chat</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Bientôt disponible</p>
        </div>
      </div>

      <form onSubmit={submit} className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70 space-y-3" data-testid="contact-form">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sujet</label>
          <input
            data-testid="contact-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Ex. Problème de connexion, question facturation..."
            className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-sm"
            required
            maxLength={200}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Message</label>
          <textarea
            data-testid="contact-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={6}
            placeholder="Décrivez votre demande..."
            className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-sm"
            required
            maxLength={5000}
          />
          <p className="text-xs text-gray-400 mt-1">{message.length}/5000</p>
        </div>
        <button
          data-testid="contact-send-btn"
          type="submit"
          disabled={sending}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold rounded-xl disabled:opacity-60"
        >
          <Send className="h-4 w-4" /> {sending ? 'Envoi...' : 'Envoyer le message'}
        </button>
      </form>
    </div>
  );
};

export default Contact;
